import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFilters } from "@/context/FiltersContext";
import { money, fmtDate } from "@/lib/ui";
import { getChoices } from "@/lib/choices";
import { isEntered, setEntered } from "@/lib/entered";
import { useAuth } from "@/context/AuthContext";
import { downloadICS } from "@/lib/ics";
import WeekendShare from "@/components/WeekendShare";
import ViewToolbar from "@/components/ViewToolbar";
import { Loader2, ChevronRight, Bell, BookmarkCheck, CalendarPlus, Navigation, Search, CheckCircle2, Circle, CalendarClock } from "lucide-react";
import RodeoActions from "@/components/RodeoActions";
import AlertsPanel from "@/components/AlertsPanel";
import { DateRangeButtons } from "@/components/DateRangeButtons";
import { toast } from "sonner";
import useIsMobile from "@/lib/useIsMobile";
import { MobileList, MobileMyRodeos, MobileDeadlines } from "@/pages/mobile/MobileSimple";

function useRodeos() {
  const [rodeos, setRodeos] = useState(null);
  useEffect(() => { api.get("/rodeos").then((r) => setRodeos(r.data)); }, []);
  return rodeos;
}

function Row({ r, right, onClick, onAssoc, showActions }) {
  return (
    <div className="border-b border-[#EEF0F2] hover:bg-[#FAFBFC]">
      <div className="w-full flex items-start gap-3 px-4 sm:px-6 py-3.5">
        <button onClick={onClick} className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-[15px]">{r.name}</span>
            {r.associations.map((a) => onAssoc ? (
              <span key={a} role="button" data-testid={`list-assoc-${a}`} onClick={(e) => { e.stopPropagation(); onAssoc(a); }}
                className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-[#D7DBE0] text-slate-600 cursor-pointer hover:border-[#C8000F] hover:text-[#C8000F]">{a}</span>
            ) : <span key={a} className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-[#D7DBE0] text-slate-600">{a}</span>)}
          </div>
          <div className="text-[12px] text-slate-500">{[r.city, r.province, r.country].filter(Boolean).join(", ")}</div>
          <div className="text-[12px] text-slate-600 mt-0.5">{r.listing ? (r.dates_text || "Schedule TBA") : <>{money(r.added_money)} Added <span className="text-slate-300">•</span> EO {fmtDate(r.entry_open)} <span className="text-slate-300">•</span> EC {fmtDate(r.entry_close)}</>}</div>
        </button>
        <button onClick={onClick} className="flex items-center gap-2 shrink-0 text-slate-500">{right}<ChevronRight className="w-4 h-4" /></button>
      </div>
      {showActions && !r.listing && <div className="px-4 sm:px-6 pb-3 -mt-1"><RodeoActions rodeo={r} /></div>}
    </div>
  );
}

export function ListView() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileList /> : <ListViewDesktop />;
}

function ListViewDesktop() {
  const nav = useNavigate();
  const { matches, assoc, toggleAssoc, sortBy, setSortBy } = useFilters();
  const rodeos = useRodeos();
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (rodeos || []).filter(matches)
      .filter((r) => !qq || `${r.name} ${r.city} ${r.state} ${(r.associations || []).join(" ")}`.toLowerCase().includes(qq))
      .sort((a, b) => sortBy === "money"
        ? (b.added_money || 0) - (a.added_money || 0)
        : (a.performances[0]?.date || "").localeCompare(b.performances[0]?.date || ""));
  }, [rodeos, matches, q, sortBy]);
  return (
    <div>
      <ViewToolbar active="/list" title="List View" />
      <div className="px-4 sm:px-6 py-3 border-b border-[#E4E7EB] flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rodeos, towns, associations…" data-testid="list-search-input"
            className="w-full rounded-lg border border-[#D7DBE0] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F]" />
        </div>
        {assoc.size > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="list-active-assoc">
            <span className="text-[11px] text-slate-400">Filtering:</span>
            {[...assoc].map((a) => (
              <button key={a} onClick={() => toggleAssoc(a)} className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-[#C8000F] text-white">{a} <span className="opacity-80">×</span></button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5 ml-auto" data-testid="list-sort">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Sort</span>
          {[{ v: "default", l: "By date" }, { v: "money", l: "Biggest purse" }].map((o) => (
            <button key={o.v} onClick={() => setSortBy(o.v)} data-testid={`list-sort-${o.v}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${sortBy === o.v ? "bg-[#3B6B1E] text-white border-[#3B6B1E]" : "bg-white text-slate-600 border-[#D7DBE0] hover:border-slate-400"}`}>{o.l}</button>
          ))}
        </div>
      </div>
      <div className="mb-4"><DateRangeButtons /></div>
      {!rodeos ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        : list.length === 0 ? <div className="text-center py-24 text-slate-500">No rodeos match your search / filters.</div>
        : list.map((r) => <Row key={r.id} r={r} onClick={() => nav(`/rodeo/${r.id}`)} onAssoc={toggleAssoc} showActions right={<span className="text-xs font-semibold text-slate-500">{sortBy === "money" ? money(r.added_money) : fmtDate(r.performances[0]?.date)}</span>} />)}
    </div>
  );
}

export function MyRodeos() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileMyRodeos /> : <MyRodeosDesktop />;
}

function MyRodeoCard({ r, choices, nav }) {
  const ch = choices[r.id] || {};
  const p1 = (r.performances || []).find((x) => x.id === ch.pref1);
  const p2 = (r.performances || []).find((x) => x.id === ch.pref2);
  const [entered, setEnt] = useState(isEntered(r.id));
  const closeIso = r.entry_close;
  const daysToClose = closeIso ? Math.ceil((new Date(closeIso + "T00:00:00") - new Date()) / 86400000) : null;
  const closed = r.status === "closed" || (daysToClose != null && daysToClose < 0);
  const toggle = () => { const v = !entered; setEntered(r.id, v); setEnt(v); toast.success(v ? "Marked as entered" : "Marked as not entered yet"); };

  return (
    <div className="border-b border-[#EEF0F2] px-4 sm:px-6 py-4" data-testid={`myrodeo-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <button onClick={() => nav(`/rodeo/${r.id}`)} className="text-left min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-[15px]">{r.name}</span>
            {(r.associations || []).map((a) => <span key={a} className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-[#D7DBE0] text-slate-600">{a}</span>)}
          </div>
          <div className="text-[12px] text-slate-500">{r.city}{r.state ? `, ${r.state}` : ""}</div>
        </button>
        <button onClick={toggle} data-testid={`myrodeo-status-${r.id}`}
          className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg ${entered ? "bg-[#3B6B1E]/12 text-[#3B6B1E]" : "bg-[#E68A2E]/12 text-[#9E0008]"}`}>
          {entered ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} {entered ? "Entered" : "Need to Enter"}
        </button>
      </div>

      <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {[[p1, "1st choice", "#3B6B1E"], [p2, "2nd choice", "#1D4ED8"]].map(([p, lbl, col], i) => (
          <div key={i} className="rounded-lg bg-[#F7F8FA] border border-[#EEF0F2] px-3 py-2">
            <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: col }}>{lbl}</div>
            {p ? (
              <div className="text-sm font-semibold text-[#14181F] flex items-center gap-1.5">
                <span>{p.label}</span>
                {p.type === "slack" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">SLACK</span>}
              </div>
            ) : <div className="text-sm text-slate-400">Not set</div>}
            {p && <div className="text-[11px] text-slate-500">You're up: {fmtDate(p.date)}{p.time ? ` · ${p.time}` : ""}</div>}
          </div>
        ))}
      </div>

      <div className="mt-2 flex items-center gap-2 text-[11px]">
        {closeIso && (
          <span className={`inline-flex items-center gap-1 ${!closed && daysToClose <= 3 ? "text-[#9E0008] font-bold" : "text-slate-500"}`}>
            <CalendarClock className="w-3.5 h-3.5" />
            {closed ? "Entries closed" : daysToClose === 0 ? "Entries close TODAY" : `Entries close in ${daysToClose} day${daysToClose === 1 ? "" : "s"} (${fmtDate(closeIso)})`}
          </span>
        )}
        {!entered && !closed && <span className="text-slate-400">· tap the badge once you've entered</span>}
      </div>
      {p1 && p2 && <div className="mt-1.5 text-[11px] text-slate-400">Your 2nd choice can overlap another performance — that's fine, it's just your backup plan until entries close and the draw is set.</div>}
    </div>
  );
}

function MyRodeosDesktop() {
  const nav = useNavigate();
  const rodeos = useRodeos();
  const choices = getChoices();
  const { user } = useAuth();
  const list = (rodeos || []).filter((r) => choices[r.id] && (choices[r.id].pref1 || choices[r.id].pref2));

  const exportCal = () => {
    const evs = [];
    list.forEach((r) => {
      const ch = choices[r.id];
      [["pref1", "1st"], ["pref2", "2nd"]].forEach(([k, lbl]) => {
        const p = r.performances.find((x) => x.id === ch[k]);
        if (p) evs.push({ title: `${r.name} — ${p.label} (${lbl} choice)`, dateISO: p.date, time: p.time, location: `${r.city}, ${r.state}`, details: `${r.associations.join("/")} · ${r.name}` });
      });
    });
    if (!evs.length) return toast.error("No entered performances yet");
    downloadICS(evs);
    toast.success(`Exported ${evs.length} events — import into Google or Apple Calendar`);
  };

  return (
    <div>
      <div className="px-4 sm:px-6 py-5 border-b border-[#E4E7EB] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><BookmarkCheck className="w-6 h-6 text-[#C8000F]" /><h1 className="font-display text-2xl font-extrabold tracking-tight">My Rodeos</h1></div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/planner")} data-testid="planner-link-btn" className="flex items-center gap-2 bg-white border border-[#D7DBE0] hover:border-slate-400 text-[#14181F] text-sm font-semibold px-4 py-2.5 rounded-lg">
            <Navigation className="w-4 h-4 text-[#C8000F]" /> Can I Make It?
          </button>
          <WeekendShare list={list} choices={choices} userName={user?.name} variant="light" />
          <button onClick={exportCal} data-testid="export-calendar-btn" className="flex items-center gap-2 bg-[#0C1017] hover:bg-black text-white text-sm font-semibold px-4 py-2.5 rounded-lg">
            <CalendarPlus className="w-4 h-4" /> Add to Calendar
          </button>
        </div>
      </div>
      {!rodeos ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        : list.length === 0 ? <div className="text-center py-24 text-slate-400">Pick performances on the Calendar to add rodeos here.</div>
        : list.map((r) => <MyRodeoCard key={r.id} r={r} choices={choices} nav={nav} />)}
    </div>
  );
}

export function Deadlines() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileDeadlines /> : <DeadlinesDesktop />;
}

function DeadlinesDesktop() {
  const nav = useNavigate();
  const rodeos = useRodeos();
  const [alerts, setAlerts] = useState([]);
  useEffect(() => { api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, []);
  const list = (rodeos || []).filter((r) => !r.listing && r.entry_close).slice().sort((a, b) => a.entry_close.localeCompare(b.entry_close));

  const enableAlerts = async () => {
    if (!("Notification" in window)) return toast.error("Notifications aren't supported on this device");
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      localStorage.setItem("rp_alerts", "1");
      new Notification("Rodeo Pilot", { body: "Deadline alerts on — we'll nudge you before entries close.", icon: "/icon-192.png" });
      toast.success("Deadline alerts enabled");
    } else toast.error("Notification permission denied");
  };

  const daysLeft = (iso) => Math.ceil((new Date(iso + "T00:00:00") - new Date()) / 86400000);

  return (
    <div>
      <div className="px-4 sm:px-6 py-5 border-b border-[#E4E7EB] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Bell className="w-6 h-6 text-[#C8000F]" /><h1 className="font-display text-2xl font-extrabold tracking-tight">Deadlines</h1></div>
        <button onClick={enableAlerts} data-testid="enable-alerts-btn" className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold px-4 py-2.5 rounded-lg">
          <Bell className="w-4 h-4" /> Enable Alerts
        </button>
      </div>
      <div className="px-4 sm:px-6 py-4 border-b border-[#E4E7EB]"><AlertsPanel /></div>
      {!rodeos ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        : <>
          {alerts.length > 0 && (
            <div className="px-4 sm:px-6 py-4 border-b border-[#E4E7EB] bg-[#FBF4F5]" data-testid="draw-alerts">
              <div className="text-[11px] font-bold tracking-wider uppercase text-[#9E0008] mb-2">Draw Changes — competitors you follow</div>
              <div className="space-y-2">
                {alerts.map((a, i) => (
                  <button key={i} onClick={() => nav(`/rodeo/${a.rodeo_id}`)} data-testid={`draw-alert-${i}`}
                    className="w-full text-left bg-white border border-[#E4E7EB] rounded-lg px-3 py-2.5 flex items-center justify-between gap-3 hover:border-slate-400">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-[#14181F]">{a.display} <span className="font-normal text-slate-500">· {a.rodeo_name}</span></div>
                      <div className="text-xs text-slate-500">{a.detail}</div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded shrink-0 ${a.type === "removed" ? "bg-slate-100 text-slate-500" : a.type === "added" ? "bg-green-100 text-green-700" : "bg-[#FBE9D6] text-[#9E0008]"}`}>{a.type}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {list.map((r) => {
            const dl = daysLeft(r.entry_close);
            const right = dl < 0
              ? <span className="text-xs text-slate-400">Closed</span>
              : <span className={`text-xs font-semibold ${dl <= 3 ? "text-[#C8000F]" : "text-slate-600"}`}>{dl === 0 ? "Closes today" : `${dl} day${dl === 1 ? "" : "s"} left`}</span>;
            return <Row key={r.id} r={r} onClick={() => nav(`/rodeo/${r.id}`)} right={right} />;
          })}
        </>}
    </div>
  );
}
